import { apiGet } from "@/lib/apiClient";

type AccountRow = {
  id?: string;
  name?: string | null;
  stage?: string | null;
  ownerId?: string | null;
};

type AccountsResponse = { accounts?: AccountRow[]; total?: number };
type LeadsResponse = { leads?: Array<{ status?: string | null }>; total?: number };

/** Overview cards without `/api/crm/kpi` (list APIs only). */
export type CrmListOverviewSnapshot = {
  stageCounts: Record<string, number>;
  namedAccountCount: number;
  openLeadRecordCount: number;
  unassignedCount: number;
  totalOpenDeals: number;
};

const OPEN_STAGES = ["new", "qualified", "meeting_booked", "proposal_sent", "negotiation"] as const;

export async function fetchCrmAccountsForAggregate(limit = 200): Promise<AccountRow[]> {
  const res = await apiGet<AccountsResponse>(`/api/crm/accounts?limit=${limit}`);
  if (!res.ok || !res.data?.accounts) return [];
  return res.data.accounts.filter((a) => a.id && !String(a.id).startsWith("lead_"));
}

/**
 * Build CRM dashboard metrics from accounts / leads / deals list routes.
 * Prefer this over `/api/crm/kpi` while that aggregate stays unused / unreliable.
 */
export async function fetchCrmOverviewFromLists(
  options: { maxAccounts?: number; includeDeals?: boolean } = {},
): Promise<{ ok: boolean; snapshot: CrmListOverviewSnapshot | null; error?: string }> {
  const maxAccounts = options.maxAccounts ?? 200;
  const includeDeals = options.includeDeals !== false;

  const [accountsRes, leadsRes] = await Promise.all([
    apiGet<AccountsResponse>(`/api/crm/accounts?limit=${maxAccounts}`),
    apiGet<LeadsResponse>("/api/crm/leads"),
  ]);

  if (!accountsRes.ok) {
    return { ok: false, snapshot: null, error: accountsRes.error || "Could not load accounts." };
  }
  if (!leadsRes.ok) {
    return { ok: false, snapshot: null, error: leadsRes.error || "Could not load leads." };
  }

  const accounts = (accountsRes.data?.accounts || []).filter(
    (a) => a.id && !String(a.id).startsWith("lead_"),
  );
  const stageCounts: Record<string, number> = {
    new: 0,
    qualified: 0,
    meeting_booked: 0,
    proposal_sent: 0,
    negotiation: 0,
    closed_won: 0,
    closed_lost: 0,
  };
  for (const account of accounts) {
    const stage = (account.stage || "new").trim() || "new";
    stageCounts[stage] = (stageCounts[stage] || 0) + 1;
  }

  const leads = leadsRes.data?.leads || [];
  const openLeadRecordCount = leads.filter((lead) => {
    const status = (lead.status || "open").toLowerCase();
    return status !== "converted" && status !== "disqualified";
  }).length;

  const unassignedCount = accounts.filter(
    (account) => !account.ownerId || !String(account.ownerId).trim(),
  ).length;

  let totalOpenDeals = 0;
  if (includeDeals && accounts.length > 0) {
    const deals = await fetchAllCrmDeals(Math.min(maxAccounts, accounts.length));
    totalOpenDeals = deals.filter((deal) => (deal.status || "open").toLowerCase() === "open").length;
  }

  return {
    ok: true,
    snapshot: {
      stageCounts,
      namedAccountCount: accounts.length,
      openLeadRecordCount,
      unassignedCount,
      totalOpenDeals,
    },
  };
}

export function openPipelineFromStageCounts(stageCounts: Record<string, number> | undefined): number {
  if (!stageCounts) return 0;
  return OPEN_STAGES.reduce((sum, stage) => sum + (stageCounts[stage] || 0), 0);
}

export type AggregatedContact = {
  id: string;
  accountId: string;
  accountName?: string;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  title?: string | null;
  linkedinUrl?: string | null;
  status?: string | null;
  kind?: string | null;
  updatedAt?: string | null;
};

export async function fetchAllCrmContacts(maxAccounts = 200): Promise<AggregatedContact[]> {
  const accounts = await fetchCrmAccountsForAggregate(maxAccounts);
  const out: AggregatedContact[] = [];
  await Promise.all(
    accounts.slice(0, maxAccounts).map(async (account) => {
      if (!account.id) return;
      const res = await apiGet<{ contacts?: AggregatedContact[] }>(
        `/api/crm/contacts?accountId=${encodeURIComponent(account.id)}`,
      );
      if (!res.ok || !res.data?.contacts) return;
      for (const c of res.data.contacts) {
        if (!c.id) continue;
        out.push({
          ...c,
          id: c.id,
          accountId: account.id,
          accountName: account.name ?? undefined,
        });
      }
    }),
  );
  return out;
}

export type AggregatedDeal = {
  id: string;
  accountId: string;
  accountName?: string;
  name?: string | null;
  stage?: string | null;
  status?: string | null;
  amount?: number | null;
  closeDate?: string | null;
  ownerId?: string | null;
};

export async function fetchAllCrmDeals(maxAccounts = 200): Promise<AggregatedDeal[]> {
  const accounts = await fetchCrmAccountsForAggregate(maxAccounts);
  const out: AggregatedDeal[] = [];
  await Promise.all(
    accounts.slice(0, maxAccounts).map(async (account) => {
      if (!account.id) return;
      const res = await apiGet<{ deals?: AggregatedDeal[] }>(
        `/api/crm/deals?accountId=${encodeURIComponent(account.id)}`,
      );
      if (!res.ok || !res.data?.deals) return;
      for (const d of res.data.deals) {
        if (!d.id) continue;
        out.push({
          ...d,
          id: d.id,
          accountId: account.id,
          accountName: account.name ?? undefined,
        });
      }
    }),
  );
  return out;
}
